import { useState } from 'react'
import { App, Button, Card, Select, Space, Table, Upload } from 'antd'
import { PaperClipOutlined, UploadOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { formatDateTime } from '@/shared/lib/format'
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  SUPPLIER_ATTACHMENT_KINDS,
  fetchSupplierAttachments,
  supplierAttachmentUrl,
  uploadSupplierAttachment,
  type AttachmentKind,
} from './supplierApi'

/** Yetkazib beruvchining barcha hujjatlari: shartnoma, hisob-faktura, yuk xati, to'lov dalili. */
export function SupplierAttachmentsCard({ supplierId }: { supplierId: string }) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<AttachmentKind>('CONTRACT')

  const attachments = useQuery({
    queryKey: queryKeys.supplier.attachments(supplierId),
    queryFn: ({ signal }) => fetchSupplierAttachments(supplierId, signal),
  })

  const upload = useMutation({
    mutationFn: (file: File) => uploadSupplierAttachment({ owner: 'supplier', supplierId }, kind, file),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.supplier.attachments(supplierId) }),
    onError: (error) => void message.error(translateError(error, t)),
  })

  const open = async (attachmentId: string) => {
    try {
      window.open(await supplierAttachmentUrl(supplierId, attachmentId), '_blank', 'noopener')
    } catch (error) {
      void message.error(translateError(error, t))
    }
  }

  return (
    <Card
      title={t('supplier.attachments.title')}
      extra={
        <Can permission="supplier.manage">
          <Space>
            <Select<AttachmentKind>
              value={kind}
              onChange={setKind}
              style={{ minWidth: 160 }}
              options={SUPPLIER_ATTACHMENT_KINDS.map((value) => ({ value, label: t(`supplier.attachmentKind.${value}`) }))}
            />
            <Upload
              accept={ATTACHMENT_ACCEPT}
              showUploadList={false}
              beforeUpload={(file) => {
                if (file.size > ATTACHMENT_MAX_BYTES) {
                  void message.warning(t('supplier.attachments.tooLarge'))
                  return false
                }
                upload.mutate(file)
                return false
              }}
            >
              <Button icon={<UploadOutlined />} loading={upload.isPending}>
                {t('supplier.attachments.upload')}
              </Button>
            </Upload>
          </Space>
        </Can>
      }
    >
      <Table
        size="small"
        rowKey="id"
        loading={attachments.isPending}
        pagination={false}
        dataSource={attachments.data ?? []}
        locale={{ emptyText: t('supplier.attachments.empty') }}
        columns={[
          { key: 'kind', title: t('supplier.attachments.kind'), render: (_, row) => t(`supplier.attachmentKind.${row.kind}`) },
          { key: 'purchase', title: t('purchase.field.number'), render: (_, row) => row.purchaseNumber ?? t('common.dash') },
          {
            key: 'file',
            title: t('supplier.attachments.file'),
            render: (_, row) => (
              <Button type="link" icon={<PaperClipOutlined />} onClick={() => void open(row.id)}>
                {row.fileName ?? t('supplier.attachments.unnamed')}
              </Button>
            ),
          },
          { key: 'by', title: t('supplier.attachments.by'), render: (_, row) => row.createdBy.name },
          { key: 'at', title: t('supplier.attachments.at'), render: (_, row) => formatDateTime(row.createdAt) },
        ]}
      />
    </Card>
  )
}
